// Getting a usable bearer token out of MSAL.
//
// Split out of auth.tsx so the recovery behaviour can be tested without
// mounting React — this is the code path that decides whether a user with an
// aged-out session gets back in or gets locked out, and it earned its own
// tests the hard way (see below).
//
// IMPORTANT: we return `idToken` (NOT `accessToken`). MSAL's
// acquireTokenSilent with Graph scopes like `User.Read` returns BOTH:
//   - `accessToken`: audience = Microsoft Graph (graph.microsoft.com)
//   - `idToken`:     audience = our SPA client ID (AZURE_CLIENT_ID)
// The backend validates `aud === AZURE_AUDIENCE`, which is the SPA's client
// ID, so the accessToken would 401 every time. The idToken is a full JWT
// signed by Microsoft carrying oid / email / name / tid — everything we need.

import { InteractionRequiredAuthError, AccountInfo } from '@azure/msal-browser';
import { msalInstance, apiTokenRequest } from './msal';

// ─── Interactive re-auth uses REDIRECT, never popup ─────────────────────
//
// This ran as acquireTokenPopup until it locked people out of the app. The
// failure, reconstructed from the production logs (12 × "[auth] JWT verify
// failed: jwt expired" inside two seconds, then silence):
//
//   1. A page load fires ~12 API calls in parallel. MSAL has a cached
//      account whose refresh token is gone, so all 12 send the stale idToken
//      and all 12 come back 401.
//   2. Each 401 asks for a forced refresh, so acquireTokenSilent throws
//      InteractionRequiredAuthError twelve times over.
//   3. Each of those called acquireTokenPopup. A popup fired from an XHR
//      handler has no user gesture behind it, so the browser blocks it — and
//      MSAL allows exactly ONE interactive request at a time, so the other
//      eleven throw interaction_in_progress and leave the interaction queue
//      wedged.
//   4. RedirectToSignIn then refuses to call loginRedirect, because it gates
//      on `inProgress === 'none'` and the queue never drains. The user sits
//      on "Checking your sign-in…" and cannot get in.
//
// A redirect can't be popup-blocked, and it's what the rest of the sign-in
// flow already uses. Coming back is usually invisible: Microsoft's SSO cookie
// re-issues the token and navigateToLoginRequestUrl returns the user to the
// page they were on.

const REAUTH_MARK = 'auth_reauth_at';
// A token still rejected after a full round-trip must not spin the browser in
// a redirect loop, so re-auth at most once a minute.
export const REAUTH_MIN_GAP_MS = 60_000;

function markReauth(): boolean {
  try {
    const last = Number(sessionStorage.getItem(REAUTH_MARK) ?? 0);
    if (Date.now() - last < REAUTH_MIN_GAP_MS) return false;
    sessionStorage.setItem(REAUTH_MARK, String(Date.now()));
  } catch { /* private mode — allow the one attempt */ }
  return true;
}

function clearReauthMark(): void {
  try { sessionStorage.removeItem(REAUTH_MARK); } catch { /* ignore */ }
}

// MSAL rejects a second interactive request while one is running. That's
// expected here — the first caller is already redirecting — so it must not be
// logged as a failure or retried.
export function isInteractionInProgress(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { errorCode?: string }).errorCode === 'interaction_in_progress'
  );
}

function reauthRedirect(account: AccountInfo): void {
  if (!markReauth()) return;
  msalInstance
    .acquireTokenRedirect({ ...apiTokenRequest, account })
    .catch((err) => {
      if (isInteractionInProgress(err)) return;
      console.error('[auth] interactive re-auth failed:', err);
    });
}

async function acquireToken(account: AccountInfo, forceRefresh: boolean): Promise<string | null> {
  try {
    const result = await msalInstance.acquireTokenSilent({
      ...apiTokenRequest,
      account,
      forceRefresh,
    });
    clearReauthMark();
    return result.idToken || result.accessToken || null;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      reauthRedirect(account);
      return null;
    }
    if (isInteractionInProgress(err)) return null;
    console.error('[auth] silent token acquisition failed:', err);
    return null;
  }
}

// Collapse concurrent callers onto one acquisition. Without this, a page
// load's dozen parallel requests each drive their own refresh — which is what
// turned one dead token into the wedge described above. A forced refresh may
// reuse another forced one, but never an unforced result; an unforced caller
// is happy with either.
let inFlight: { forced: boolean; promise: Promise<string | null> } | null = null;

export async function acquireTokenDeduped(
  account: AccountInfo,
  forceRefresh = false,
): Promise<string | null> {
  if (inFlight && (inFlight.forced || !forceRefresh)) return inFlight.promise;
  const entry = { forced: forceRefresh, promise: acquireToken(account, forceRefresh) };
  inFlight = entry;
  try {
    return await entry.promise;
  } finally {
    if (inFlight === entry) inFlight = null;
  }
}

// Tests only — the in-flight slot is module state that would otherwise leak
// between cases.
export function __resetTokenStateForTests(): void {
  inFlight = null;
  clearReauthMark();
}
